/* Responsive sprite sheets with baked backgrounds. Render only on demand. */
(() => {
  if (window.ObjectivShopSprites) {
    window.ObjectivShopSprites.mount(document);
    return;
  }
  const instances = new Map();

  function initialise(canvas) {
    const card = canvas.closest('.obj-shop__product');
    const media = card?.querySelector('.obj-shop__media');
    window.ObjectivModeMedia?.mountPoster(card?.querySelector('[data-shop-poster]'));
    const mediaLink = card?.querySelector('.obj-shop__media-link');
    const scrubber = card?.querySelector('.obj-shop__scrubber');
    const knob = card?.querySelector('.obj-shop__scrubber-knob');
    const fallback = card?.querySelector('.obj-shop__media-fallback');
    const hoverImage = card?.querySelector('.obj-shop__hover-image');
    const source = card?.querySelector('.obj-shop__sprite-sources');
    const ctx = canvas.getContext('2d');
    if (!ctx || !source || !media || !knob || !scrubber) return null;
    let config;
    try { config = JSON.parse(source.textContent); } catch (_) { return null; }
    const steps = config.frames - 1;
    const initialFrame = Math.max(0, Math.min(steps, config.initialFrame ?? 7));
    const lifetime = new AbortController();
    const cache = new Map();
    const files = new Map();
    const compressedLimit = 40 * 1024 * 1024;
    let compressedBytes = 0;
    let fetches = 0;
    let generation = 0;
    let preparedWidth = 0;
    let engaged = false;
    let drawing = false;
    let decodeTail = Promise.resolve();
    let direction = 1;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let mode = document.documentElement.dataset.colorMode === 'dark' ? 'dark' : 'light';
    let currentFrame = initialFrame;
    let knobX = 0;
    let travel = 1;
    let knobDragging = false;
    let mediaSwiping = false;
    let mediaPointer = null;
    let knobPointer = null;
    let hasSwiped = false;
    let startX = 0;
    let startY = 0;
    let startKnobX = 0;
    let startFrame = initialFrame;
    let visible = !window.IntersectionObserver;
    let failed = false;
    let disposed = false;
    let raf = null;
    let requestVersion = 0;
    let lastRenderedKey = '';
    let resolutionQuery;
    let observer;
    let resizeObserver;
    const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: lifetime.signal });
    if (hoverImage) {
      listen(hoverImage, 'error', () => hoverImage.classList.add('is-unavailable'));
      if (hoverImage.complete && !hoverImage.naturalWidth) hoverImage.classList.add('is-unavailable');
    }
    const maxTravel = () => travel;
    const activeTiers = () => mode === 'light' && config.lightTiers ? config.lightTiers : config.tiers;
    const tierForCanvas = () => {
      const requiredWidth = Math.min(canvas.width, canvas.height * 3 / 4);
      const tiers = activeTiers();
      return tiers.find(tier => tier.width >= requiredWidth) || tiers.at(-1);
    };
    const sheetKey = (tier, index) => `${mode}:${tier.width}:${index}`;
    const desiredKey = () => sheetKey(tierForCanvas(), Math.floor(currentFrame / config.framesPerSheet));
    const release = bitmap => {
      if (bitmap?.close) bitmap.close();
      else if (bitmap) bitmap.src = '';
    };

    function clearMedia() {
      generation += 1;
      for (const entry of cache.values()) { entry.controller.abort(); release(entry.bitmap); }
      cache.clear();
      for (const entry of files.values()) {
        entry.controller.abort();
        entry.reject(new DOMException('Cancelled', 'AbortError'));
      }
      files.clear();
      compressedBytes = 0;
      canvas.dataset.cachedSheets = '0';
      canvas.dataset.compressedBytes = '0';
    }

    function pumpFiles() {
      if (disposed || failed || !visible || document.hidden) return;
      while (fetches < 4) {
        const desired = Math.floor(currentFrame / config.framesPerSheet);
        const entry = [...files.values()].filter(item => item.state === 'queued')
          .sort((a, b) => b.priority - a.priority || Math.abs(a.index - desired) - Math.abs(b.index - desired))[0];
        if (!entry) break;
        entry.state = 'loading';
        fetches += 1;
        fetch(entry.url, { signal: entry.controller.signal }).then(async response => {
          if (!response.ok) throw new Error(`Sprite response ${response.status}`);
          const blob = await response.blob();
          if (entry.controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
          // Keep compressed transfers separate from the two decoded sheets. The
          // current 11-sheet tiers fit within this cap, including the 1200 tier.
          while (compressedBytes + blob.size > compressedLimit) {
            const victim = [...files.values()].find(item => item.state === 'ready' && item.key !== desiredKey());
            if (!victim) throw new Error('Sprite compressed budget exceeded');
            files.delete(victim.key);
            compressedBytes -= victim.bytes;
          }
          entry.state = 'ready';
          entry.bytes = blob.size;
          compressedBytes += blob.size;
          canvas.dataset.compressedBytes = String(compressedBytes);
          entry.resolve(blob);
          requestDraw();
        }).catch(error => {
          entry.state = 'error';
          entry.reject(error);
          if (!entry.controller.signal.aborted) requestDraw();
        }).finally(() => {
          fetches -= 1;
          pumpFiles();
        });
      }
    }

    function file(tier, index, priority = 0) {
      const key = sheetKey(tier, index);
      if (!files.has(key)) {
        const entry = { key, index, url: tier.urls[index], priority, state: 'queued', bytes: 0, controller: new AbortController() };
        entry.promise = new Promise((resolve, reject) => { entry.resolve = resolve; entry.reject = reject; });
        // Preparation failures only affect the UI if that sheet is requested.
        entry.promise.catch(() => {});
        files.set(key, entry);
      }
      const entry = files.get(key);
      entry.priority = Math.max(entry.priority, priority);
      pumpFiles();
      return entry.promise;
    }

    function prepare() {
      if (!visible || failed || disposed) return;
      const tier = tierForCanvas();
      if (preparedWidth !== tier.width) {
        clearMedia();
        preparedWidth = tier.width;
      }
      const index = Math.floor(currentFrame / config.framesPerSheet);
      file(tier, index, 1);
      // Opening needs one pose, not all eleven sheets. Warm the full sequence
      // on interaction so images/fonts do not compete with megabytes of frames.
      if (engaged && !navigator.connection?.saveData) {
        for (let distance = 1; distance < tier.urls.length; distance++) {
          for (const next of [index + distance, index - distance]) {
            if (next >= 0 && next < tier.urls.length) file(tier, next);
          }
        }
      }
    }

    function updatePoster() {
      if (!fallback || !activeTiers()[0].poster) return;
      const srcset = activeTiers().map(tier => `${tier.poster} ${tier.width}w`).join(', ');
      fallback.srcset = srcset;
      fallback.src = activeTiers()[0].poster;
    }

    const warmSequence = () => {
      if (engaged) return;
      engaged = true;
      prepare();
    };
    listen(media, 'pointerenter', event => { if (event.pointerType === 'mouse') warmSequence(); });
    listen(media, 'pointerdown', warmSequence);
    listen(scrubber, 'pointerdown', warmSequence);
    listen(scrubber, 'focusin', warmSequence);

    function changeMode() {
      const next = document.documentElement.dataset.colorMode === 'dark' ? 'dark' : 'light';
      if (next === mode) return;
      mode = next;
      // Invalidate old asynchronous draws before releasing their mode's sheets.
      requestVersion += 1;
      clearMedia();
      preparedWidth = 0;
      lastRenderedKey = '';
      failed = false;
      delete canvas.dataset.spriteError;
      canvas.classList.remove('is-loaded');
      if (fallback) fallback.style.display = 'block';
      knob.removeAttribute('aria-disabled');
      knob.setAttribute('tabindex', '0');
      updatePoster();
      prepare();
      updateFrame(currentFrame);
    }
    updatePoster();
    listen(document, 'objectiv:color-mode-change', changeMode);

    function imageFallback(url, signal) {
      return new Promise((resolve, reject) => {
        if (signal.aborted) { reject(new DOMException('Cancelled', 'AbortError')); return; }
        const image = new Image();
        image.decoding = 'async';
        const cancel = () => { image.src = ''; reject(new DOMException('Cancelled', 'AbortError')); };
        signal.addEventListener('abort', cancel, { once: true });
        image.onload = () => { signal.removeEventListener('abort', cancel); resolve(image); };
        image.onerror = () => { signal.removeEventListener('abort', cancel); reject(new Error('Sprite image unavailable')); };
        image.src = url;
      });
    }

    async function decode(tier, index, signal) {
      try {
        const blob = await file(tier, index, 2);
        if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
        if (window.createImageBitmap) {
          const bitmap = await createImageBitmap(blob);
          if (signal.aborted) { bitmap.close(); throw new DOMException('Cancelled', 'AbortError'); }
          return bitmap;
        }
        const url = URL.createObjectURL(blob);
        try { return await imageFallback(url, signal); }
        finally { URL.revokeObjectURL(url); }
      } catch (error) {
        if (signal.aborted) throw error;
        // Also supports older browsers and CDN responses without fetch CORS.
        return imageFallback(tier.urls[index], signal);
      }
    }

    function sheet(tier, index) {
      const key = sheetKey(tier, index);
      if (cache.has(key)) {
        const entry = cache.get(key);
        cache.delete(key);
        cache.set(key, entry);
        return entry.promise;
      }
      const entry = { controller: new AbortController(), bitmap: null };
      // Evict before starting the next native decode, retaining at most two
      // decoded/pending sheets. Compressed downloads continue independently.
      while (cache.size >= 2) {
        const victimKey = cache.keys().next().value;
        const victim = cache.get(victimKey);
        cache.delete(victimKey);
        victim.controller.abort();
        release(victim.bitmap);
      }
      entry.promise = decodeTail.then(() => {
        if (entry.controller.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
        return decode(tier, index, entry.controller.signal);
      }).then(bitmap => {
        if (disposed || entry.controller.signal.aborted) {
          release(bitmap);
          throw new DOMException('Cancelled', 'AbortError');
        }
        entry.bitmap = bitmap;
        return bitmap;
      });
      decodeTail = entry.promise.catch(() => {});
      cache.set(key, entry);
      canvas.dataset.cachedSheets = String(cache.size);
      return entry.promise;
    }

    function prepareNeighbour(tier, index) {
      const next = index + direction;
      if (!navigator.connection?.saveData && next >= 0 && next < tier.urls.length &&
          files.get(sheetKey(tier, next))?.state === 'ready') {
        sheet(tier, next).catch(() => {});
      }
    }

    function showFallback() {
      failed = true;
      canvas.classList.remove('is-loaded');
      canvas.dataset.spriteError = 'true';
      if (fallback) fallback.style.display = 'block';
      knob.setAttribute('aria-disabled', 'true');
      knob.setAttribute('tabindex', '-1');
      knob.setAttribute('aria-valuenow', String(initialFrame));
      knob.setAttribute('aria-valuetext', `Frame ${initialFrame + 1} of ${config.frames}; animation unavailable`);
      clearMedia();
    }

    async function draw(version) {
      if (drawing || !visible || failed || disposed || document.hidden || !canvas.width || !canvas.height) return;
      const tier = tierForCanvas();
      const epoch = generation;
      const index = Math.floor(currentFrame / config.framesPerSheet);
      if (`${mode}:${currentFrame}:${tier.width}:${canvas.width}:${canvas.height}` === lastRenderedKey) {
        prepareNeighbour(tier, index);
        return;
      }
      // A network wait must not monopolise the decoder while input moves to
      // another sheet. Downloads notify us on completion; decoding stays serial.
      file(tier, index, 2);
      const pending = files.get(sheetKey(tier, index));
      if (pending.state === 'queued' || pending.state === 'loading') return;
      drawing = true;
      try {
        const bitmap = await sheet(tier, index);
        if (disposed || !visible || epoch !== generation) return;
        // Pointer events can advance within this same sheet during decoding.
        // Paint the latest frame, rather than invalidating every pending draw.
        const frame = Math.max(index * config.framesPerSheet,
          Math.min(currentFrame, steps, (index + 1) * config.framesPerSheet - 1));
        if ((knobDragging || mediaSwiping) &&
            ((direction > 0 && frame > currentFrame) || (direction < 0 && frame < currentFrame))) return;
        // A decode may straddle a sheet boundary during a continuous drag.
        // While actively dragging, show the newest completed sheet instead of
        // starving the display on a slow decoder. Direct/released seeks reject
        // distant obsolete poses and always settle at the exact latest target.
        if (!knobDragging && !mediaSwiping && Math.abs(frame - currentFrame) > config.framesPerSheet * 2) return;
        const local = frame % config.framesPerSheet;
        const width = bitmap.width / config.cols;
        const height = bitmap.height / config.rows;
        const scale = Math.min(canvas.width / width, canvas.height / height);
        const dw = width * scale;
        const dh = height * scale;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, (local % config.cols) * width,
          Math.floor(local / config.cols) * height, width, height,
          (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
        lastRenderedKey = `${mode}:${frame}:${tier.width}:${canvas.width}:${canvas.height}`;
        canvas.dataset.frame = String(frame);
        canvas.dataset.spriteMode = mode;
        canvas.dataset.sourceWidth = String(tier.width);
        canvas.classList.add('is-loaded');
        if (fallback) fallback.style.display = 'none';
        prepareNeighbour(tier, index);
      } catch (error) {
        if (!disposed && epoch === generation && index === Math.floor(currentFrame / config.framesPerSheet) && error.name !== 'AbortError') showFallback();
      } finally {
        drawing = false;
        if (version !== requestVersion) requestDraw();
      }
    }

    function requestDraw() {
      requestVersion += 1;
      if (drawing || raf !== null || failed || disposed || !visible) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        void draw(requestVersion);
      });
    }

    function updateFrame(frame, animate = false) {
      if (failed || disposed) return;
      const next = Math.max(0, Math.min(steps, Math.round(frame)));
      if (next !== currentFrame) direction = next > currentFrame ? 1 : -1;
      currentFrame = next;
      knobX = (currentFrame / steps) * maxTravel();
      knob.style.transition = animate && !reducedMotion.matches ? 'transform 0.2s ease' : 'none';
      knob.style.transform = `translate3d(${knobX}px, 0, 0)`;
      knob.setAttribute('aria-valuemax', String(steps));
      knob.setAttribute('aria-valuenow', String(currentFrame));
      knob.setAttribute('aria-valuetext', `Frame ${currentFrame + 1} of ${config.frames}`);
      requestDraw();
    }

    function resize() {
      if (disposed) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      travel = Math.max(1, scrubber.getBoundingClientRect().width - knob.getBoundingClientRect().width);
      let dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      dpr = Math.min(dpr, Math.sqrt(4000000 / (rect.width * rect.height)));
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        lastRenderedKey = '';
      }
      prepare();
      updateFrame(currentFrame);
    }

    function watchResolution() {
      if (resolutionQuery) resolutionQuery.removeEventListener('change', resolutionChanged);
      resolutionQuery = matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      resolutionQuery.addEventListener('change', resolutionChanged);
    }
    function resolutionChanged() { resize(); watchResolution(); }
    listen(window, 'resize', resize);
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(canvas);
      resizeObserver.observe(scrubber);
    }
    if (window.IntersectionObserver) {
      observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        if (visible) resize();
      }, { rootMargin: '100px' });
      observer.observe(media);
    }
    watchResolution();
    listen(document, 'visibilitychange', () => {
      if (!document.hidden && visible) { prepare(); requestDraw(); }
    });

    const capture = (element, event) => { try { element.setPointerCapture(event.pointerId); } catch (_) {} };
    const uncapture = (element, event) => { try { element.releasePointerCapture(event.pointerId); } catch (_) {} };
    // Native link/image dragging otherwise cancels the pointer after the first move.
    listen(media, 'dragstart', event => event.preventDefault());
    if (mediaLink) listen(mediaLink, 'dragstart', event => event.preventDefault());
    listen(media, 'pointerdown', event => {
      // The cart overlay is an independent control, not a swipe surface.
      if (event.target.closest('.obj-shop__quick-add')) return;
      if (failed || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (!event.isPrimary) { mediaSwiping = false; hasSwiped = false; return; }
      mediaSwiping = true;
      mediaPointer = event.pointerId;
      hasSwiped = false;
      startX = event.clientX;
      startY = event.clientY;
      startFrame = currentFrame;
      capture(mediaLink || media, event);
    });
    listen(media, 'pointermove', event => {
      if (!mediaSwiping || event.pointerId !== mediaPointer) return;
      const delta = event.clientX - startX;
      if (Math.hypot(delta, event.clientY - startY) > 6) hasSwiped = true;
      if (Math.abs(delta) <= 6 || Math.abs(delta) <= Math.abs(event.clientY - startY)) return;
      media.classList.add('is-scrubbing');
      updateFrame(startFrame + delta / Math.max(1, media.clientWidth) * steps);
    });
    const endSwipe = event => {
      if (event.pointerId !== mediaPointer) return;
      mediaSwiping = false;
      mediaPointer = null;
      media.classList.remove('is-scrubbing');
      uncapture(mediaLink || media, event);
    };
    listen(media, 'pointerup', endSwipe);
    listen(media, 'pointercancel', event => {
      if (event.pointerId === mediaPointer) hasSwiped = true;
      endSwipe(event);
    });
    listen(media, 'lostpointercapture', () => { mediaSwiping = false; media.classList.remove('is-scrubbing'); });
    if (mediaLink) listen(mediaLink, 'click', event => {
      if (hasSwiped) { event.preventDefault(); hasSwiped = false; }
    });

    listen(knob, 'pointerdown', event => {
      if (failed || event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      event.stopPropagation();
      knob.focus();
      knobDragging = true;
      knobPointer = event.pointerId;
      startX = event.clientX;
      startKnobX = knobX;
      capture(knob, event);
    });
    listen(knob, 'pointermove', event => {
      if (!knobDragging || event.pointerId !== knobPointer) return;
      updateFrame((startKnobX + event.clientX - startX) / maxTravel() * steps);
    });
    const endKnob = event => {
      if (event.pointerId !== knobPointer) return;
      knobDragging = false;
      knobPointer = null;
      uncapture(knob, event);
    };
    listen(knob, 'pointerup', endKnob);
    listen(knob, 'pointercancel', endKnob);
    listen(knob, 'lostpointercapture', () => { knobDragging = false; });
    listen(scrubber, 'pointerdown', event => {
      if (failed || knob.contains(event.target) || event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      knob.focus();
      const rect = scrubber.getBoundingClientRect();
      updateFrame((event.clientX - rect.left - knob.getBoundingClientRect().width / 2) / maxTravel() * steps, true);
      knobDragging = true;
      knobPointer = event.pointerId;
      startX = event.clientX;
      startKnobX = knobX;
      capture(knob, event);
    });
    listen(knob, 'keydown', event => {
      const targets = { ArrowRight: currentFrame + 1, ArrowUp: currentFrame + 1,
        ArrowLeft: currentFrame - 1, ArrowDown: currentFrame - 1,
        PageUp: currentFrame + 5, PageDown: currentFrame - 5, Home: 0, End: steps };
      if (!(event.key in targets) || failed) return;
      event.preventDefault();
      updateFrame(targets[event.key]);
    });
    resize();
    return {
      resize,
      dispose() {
        disposed = true;
        lifetime.abort();
        if (raf !== null) cancelAnimationFrame(raf);
        observer?.disconnect();
        resizeObserver?.disconnect();
        resolutionQuery?.removeEventListener('change', resolutionChanged);
        clearMedia();
      }
    };
  }

  function mount(root) {
    root.querySelectorAll('.obj-shop__unboxing-canvas').forEach(canvas => {
      if (!instances.has(canvas)) {
        const instance = initialise(canvas);
        if (instance) instances.set(canvas, instance);
      }
    });
  }
  window.ObjectivShopSprites = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [canvas, instance] of instances) {
      if (event.target.contains(canvas)) { instance.dispose(); instances.delete(canvas); }
    }
  });
  window.addEventListener('pageshow', () => { for (const instance of instances.values()) instance.resize(); });
})();
